import React, { useState, useEffect, useCallback, useRef } from 'react';

const BackgroundDoodles = ({
  shapes = ['circle', 'triangle', 'star', 'square', 'hexagon', 'diamond', 'cross', 'zigzag', 'wave'],
  density = 15,
  colors = ['#e9e9e9', '#d4d4d4', '#cfcfcf'],
  minSize = 10,
  maxSize = 50,
  opacity = 0.4,
  animate = true,
  animationSpeed = 1, // 1 is normal speed, 0.5 is half speed, 2 is double speed
  strokeWidth = 2,
  outlineOnly = true
}) => {
  const [doodles, setDoodles] = useState([]);
  const isInitialMount = useRef(true);
  const previousProps = useRef({
    density,
    shapes: [...shapes],
    colors: [...colors],
    minSize,
    maxSize,
    animate,
    animationSpeed,
    strokeWidth,
    outlineOnly
  });
  
  // Using useCallback to memoize the generateDoodles function
  const generateDoodles = useCallback(() => {
    console.log("Generating doodles with:", { density, shapes, colors, minSize, maxSize });
    const newDoodles = [];
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    for (let i = 0; i < density; i++) {
      const randomShape = shapes[Math.floor(Math.random() * shapes.length)];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];
      const randomSize = Math.floor(Math.random() * (maxSize - minSize)) + minSize;
      const randomX = Math.floor(Math.random() * windowWidth);
      const randomY = Math.floor(Math.random() * windowHeight);
      const randomRotation = Math.floor(Math.random() * 360);
      const baseDuration = Math.floor(Math.random() * 20) + 10; // Base animation duration
      const adjustedDuration = baseDuration / animationSpeed; // Adjust by speed factor
      const randomDelay = Math.floor(Math.random() * 5) / animationSpeed; // Adjust delay too
      
      newDoodles.push({
        shape: randomShape,
        color: randomColor,
        size: randomSize,
        x: randomX,
        y: randomY,
        rotation: randomRotation,
        duration: adjustedDuration,
        delay: randomDelay,
        id: `doodle-${i}-${Date.now()}`
      });
    }
    
    setDoodles(newDoodles);
  }, [density, shapes, colors, minSize, maxSize, animationSpeed]);

  // Check if the props that affect doodle generation have changed
  const havePropsChanged = useCallback(() => {
    if (density !== previousProps.current.density) return true;
    if (minSize !== previousProps.current.minSize) return true;
    if (maxSize !== previousProps.current.maxSize) return true;
    if (animationSpeed !== previousProps.current.animationSpeed) return true;
    if (shapes.length !== previousProps.current.shapes.length) return true;
    if (colors.length !== previousProps.current.colors.length) return true;
    
    // Check if shapes array has changed
    for (let i = 0; i < shapes.length; i++) {
      if (shapes[i] !== previousProps.current.shapes[i]) return true;
    }
    
    // Check if colors array has changed
    for (let i = 0; i < colors.length; i++) {
      if (colors[i] !== previousProps.current.colors[i]) return true;
    }
    
    return false;
  }, [density, shapes, colors, minSize, maxSize, animationSpeed]);
  
  // Only generate doodles on initial mount or when specific props change
  useEffect(() => {
    if (isInitialMount.current) {
      console.log("Initial mount - generating doodles");
      generateDoodles();
      isInitialMount.current = false;
    } else if (havePropsChanged()) {
      console.log("Props changed - regenerating doodles");
      generateDoodles();
    }
    
    // Update the previous props reference
    previousProps.current = {
      density,
      shapes: [...shapes],
      colors: [...colors],
      minSize,
      maxSize,
      animate,
      animationSpeed,
      strokeWidth,
      outlineOnly
    };
  }, [density, shapes, colors, minSize, maxSize, animate, animationSpeed, strokeWidth, outlineOnly, generateDoodles, havePropsChanged]);
  
  // Handle window resize separately
  useEffect(() => {
    const handleResize = () => {
      console.log("Window resized - regenerating doodles");
      generateDoodles();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [generateDoodles]);
  
  const renderShape = (doodle) => {
    const { shape, color, size } = doodle;
    const fillColor = outlineOnly ? 'transparent' : color;
    const borderColor = color;
    
    switch (shape) {
      case 'circle':
        return (
          <div
            style={{
              width: `${size}px`,
              height: `${size}px`,
              border: `${strokeWidth}px solid ${borderColor}`,
              borderRadius: '50%',
              backgroundColor: fillColor,
            }}
          />
        );
      case 'triangle':
        return (
          <div
            style={{
              width: `${size}px`,
              height: `${size}px`,
              position: 'relative',
            }}
          >
            <svg 
              width={size} 
              height={size} 
              viewBox="0 0 100 100"
              style={{ display: 'block' }}
            >
              <polygon 
                points="50,10 10,90 90,90" 
                fill={fillColor} 
                stroke={borderColor} 
                strokeWidth={strokeWidth * 3} 
              />
            </svg>
          </div>
        );
      case 'star':
        return (
          <div
            style={{
              width: `${size}px`,
              height: `${size}px`,
              position: 'relative',
            }}
          >
            <svg 
              width={size} 
              height={size} 
              viewBox="0 0 100 100"
              style={{ display: 'block' }}
            >
              <polygon 
                points="50,5 61,40 98,40 68,62 79,95 50,75 21,95 32,62 2,40 39,40" 
                fill={fillColor} 
                stroke={borderColor} 
                strokeWidth={strokeWidth * 3} 
              />
            </svg>
          </div>
        );
      case 'square':
        return (
          <div
            style={{
              width: `${size}px`,
              height: `${size}px`,
              border: `${strokeWidth}px solid ${borderColor}`,
              backgroundColor: fillColor,
            }}
          />
        );
      case 'hexagon':
        return (
          <div
            style={{
              width: `${size}px`,
              height: `${size}px`,
              position: 'relative',
            }}
          >
            <svg 
              width={size} 
              height={size} 
              viewBox="0 0 100 100"
              style={{ display: 'block' }}
            >
              <polygon 
                points="50,3 90,25 90,75 50,97 10,75 10,25" 
                fill={fillColor} 
                stroke={borderColor} 
                strokeWidth={strokeWidth * 3} 
              />
            </svg>
          </div>
        );
      case 'diamond':
        return (
          <div
            style={{
              width: `${size}px`,
              height: `${size}px`,
              position: 'relative',
            }}
          >
            <svg 
              width={size} 
              height={size} 
              viewBox="0 0 100 100"
              style={{ display: 'block' }}
            >
              <polygon 
                points="50,5 95,50 50,95 5,50" 
                fill={fillColor} 
                stroke={borderColor} 
                strokeWidth={strokeWidth * 3} 
              />
            </svg>
          </div>
        );
      case 'cross':
        return (
          <div
            style={{
              width: `${size}px`,
              height: `${size}px`,
              position: 'relative',
            }}
          >
            <svg 
              width={size} 
              height={size} 
              viewBox="0 0 100 100"
              style={{ display: 'block' }}
            >
              <path 
                d="M35,5 L65,5 L65,35 L95,35 L95,65 L65,65 L65,95 L35,95 L35,65 L5,65 L5,35 L35,35 Z" 
                fill={fillColor} 
                stroke={borderColor} 
                strokeWidth={strokeWidth * 3} 
              />
            </svg>
          </div>
        );
      case 'zigzag':
        return (
          <div
            style={{
              width: `${size}px`,
              height: `${size}px`,
              position: 'relative',
            }}
          >
            <svg 
              width={size} 
              height={size} 
              viewBox="0 0 100 100"
              style={{ display: 'block' }}
            >
              <polyline 
                points="5,50 25,30 45,50 65,30 85,50 25,70 45,90 65,70 85,90" 
                fill="none" 
                stroke={borderColor} 
                strokeWidth={strokeWidth * 3} 
              />
            </svg>
          </div>
        );
      case 'wave':
        return (
          <div
            style={{
              width: `${size}px`,
              height: `${size}px`,
              position: 'relative',
            }}
          >
            <svg 
              width={size} 
              height={size} 
              viewBox="0 0 100 100"
              style={{ display: 'block' }}
            >
              <path 
                d="M5,50 C20,20 35,80 50,50 C65,20 80,80 95,50" 
                fill="none" 
                stroke={borderColor} 
                strokeWidth={strokeWidth * 3} 
              />
            </svg>
          </div>
        );
      default:
        return (
          <div
            style={{
              width: `${size}px`,
              height: `${size}px`,
              border: `${strokeWidth}px solid ${borderColor}`,
              borderRadius: '50%',
              backgroundColor: fillColor,
            }}
          />
        );
    }
  };

  // Create a global style for all animations
  const globalStyles = animate ? (
    <style>
      {doodles.map((doodle) => {
        const animationId = `rotate-${doodle.id}`;
        return `
          @keyframes ${animationId} {
            0% { transform: rotate(${doodle.rotation}deg); }
            50% { transform: rotate(${doodle.rotation + 180}deg); }
            100% { transform: rotate(${doodle.rotation}deg); }
          }
        `;
      }).join("")}
    </style>
  ) : null;
  
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none', // Allows clicks to pass through
        zIndex: -1, // Ensure it's behind other content
        overflow: 'hidden',
      }}
    >
      {globalStyles}
      {doodles.map((doodle) => {
        const animationId = `rotate-${doodle.id}`;
        return (
          <div
            key={doodle.id}
            style={{
              position: 'absolute',
              left: `${doodle.x}px`,
              top: `${doodle.y}px`,
              opacity: opacity,
              transform: !animate ? `rotate(${doodle.rotation}deg)` : undefined,
              animation: animate ? `${animationId} ${doodle.duration}s ease-in-out infinite ${doodle.delay}s` : undefined,
            }}
          >
            {renderShape(doodle)}
          </div>
        );
      })}
    </div>
  );
};

export default BackgroundDoodles;